/**
 * BackToTop — floating pill button anchored to the right edge that
 * scrolls the page smoothly to the top after a threshold.
 *
 * • Appears only once the user has scrolled > 400px so it doesn't
 *   clutter the first viewport.
 * • Uses `prefers-reduced-motion` to honour user OS settings.
 * • Positioned above other floating widgets (Report / Sonner) via
 *   z-40 + bottom offset that stacks predictably on mobile.
 */
import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTop = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={goTop}
      data-testid="back-to-top"
      className={`fixed right-4 sm:right-6 bottom-44 z-50 inline-flex items-center gap-2 rounded-full bg-[#1E64C8] text-white shadow-lg hover:bg-[#164FA0] hover:shadow-xl px-4 py-3 text-sm font-semibold transition-all duration-300 ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none"
      }`}
    >
      <ArrowUp className="w-4 h-4" />
      <span className="hidden sm:inline">Top</span>
    </button>
  );
}
